import Cookies from "js-cookie";
import type { PointerEvent as ReactPointerEvent } from "react";
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IUniversalItem } from "userful-chronos-app-common-js/dist/itemsControl/itemsControl";
import { ITable, ITableHeader, ITableRow } from "userful-chronos-app-common-js/dist/models/table/userfulTableInterface";
import {
    ItemsControlActions,
    useItemsControlDispatch,
    useItemsControlSelector,
} from "userful-chronos-common-store/dist/itemsControl";
import ColumnSelector from "./columnSelector/ColumnSelector";
import TableHeaderFill from "./fill/TableHeaderFill";
import TableHeader from "./header/TableHeader";
import UserfulTablePagination, { IPagination } from "./pagination/UserfulTablePagination";
import TableRow from "./row/TableRow";
import TableMultiselect from "./select/multiselect/TableMultiselect";
import { getComponentsValue } from "./utils/component";

import { Checkbox, ClickAndDragItem, FlyContainer } from "userful-chronos-common-ui/dist/elements";
import "./UserfulTable2026.scss";

export type SortState = { id: string; direction: "asc" | "desc" };

interface IProps {
    table: ITable;
    selection?: boolean;
    sorting?: boolean;
    onRowSingleClick?: (row: ITableRow, index: number) => void;
    onRowDoubleClick?: (row: ITableRow, index: number) => void;
    onRowDrop?: (row: ITableRow, items: IUniversalItem[]) => void;
    onRowIdle?: (row: ITableRow, items: IUniversalItem[]) => void;
    acceptedDropTypes?: string[];
}

export default function UserfulTable({
    table,
    selection = true,
    sorting = true,
    onRowSingleClick,
    onRowDoubleClick,
    onRowDrop,
    onRowIdle,
    acceptedDropTypes,
}: IProps) {
    const tableName = table?.id || (table as any)?.name || "UserfulTable";
    const tableRef = useRef<HTMLDivElement | null>(null);
    const columnRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const columnDragListenersRef = useRef<{ move?: (event: PointerEvent) => void; up?: (event: PointerEvent) => void }>(
        {},
    );

    const itemsControlDispatch = useItemsControlDispatch();

    const selectedItems = useItemsControlSelector((state) => state.itemsControl.selectedItems);
    const draggedItems = useItemsControlSelector((state) => state.itemsControl.draggedItems);

    const rawHeaders: any[] = useMemo(() => {
        return Array.isArray(table?.headers)
            ? table.headers
            : Array.isArray((table as any)?.columns)
              ? (table as any).columns
              : [];
    }, [table?.headers, (table as any)?.columns]);

    const tableColumns: Array<ITableHeader & { header: string }> = useMemo(
        () =>
            rawHeaders.map((header, index) => {
                const headerId =
                    typeof header.id !== "undefined" && header.id !== null
                        ? header.id
                        : header.header || `col-${index}`;
                const headerComponent = header.component ?? header.header ?? headerId;
                const headerLabel =
                    typeof headerComponent === "string" || typeof headerComponent === "number"
                        ? String(headerComponent)
                        : String(headerId);

                return {
                    ...header,
                    id: headerId,
                    header: headerLabel,
                };
            }),
        [rawHeaders],
    );

    const getColumnKey = (col: { id?: any; header?: any }, fallback?: number) => {
        if (typeof col?.id !== "undefined" && col?.id !== null) {
            return String(col.id);
        }

        if (typeof fallback !== "undefined" && fallback !== null) {
            return String(fallback);
        }

        if (typeof col?.header !== "undefined" && col?.header !== null) {
            return String(col.header);
        }

        return "";
    };

    const tableData = Array.isArray(table?.rows)
        ? table.rows
        : Array.isArray((table as any)?.data)
          ? (table as any).data
          : [];
    const DEFAULT_PAGE_SIZE = 15;

    const [columnOrder, setColumnOrder] = useState<string[]>([]);
    const [columnSize, setColumnSize] = useState<Record<string, number>>({});
    const [columnSelector, setColumnSelector] = useState<Record<string, boolean>>({});
    const [sort, setSort] = useState<SortState | undefined>(undefined);
    const [columnDrag, setColumnDrag] = useState<{ key: string; startX: number; currentX: number } | null>(null);
    const [dropIndicatorIndex, setDropIndicatorIndex] = useState<number | null>(null);
    const [paginationState, setPaginationState] = useState<{ currentPage: number; dataPerPage: number }>({
        currentPage: 1,
        dataPerPage: DEFAULT_PAGE_SIZE,
    });

    const columnKeys = useMemo(() => tableColumns.map((col, index) => getColumnKey(col, index)), [tableColumns]);
    const columnKeySignature = useMemo(() => columnKeys.join("|"), [columnKeys]);

    const columnIndexMap = useMemo(() => {
        const map = new Map<string, number>();
        tableColumns.forEach((col, index) => {
            map.set(getColumnKey(col, index), index);
        });
        return map;
    }, [tableColumns]);

    const findRowColumnById = useCallback(
        (row: ITableRow, columnId: string) => {
            if (!row?.columns || !Array.isArray(row.columns)) {
                return undefined;
            }
            const normalizedId = String(columnId);
            const idx = columnIndexMap.get(normalizedId);
            if (typeof idx === "number") {
                const byIndex = row.columns[idx];
                if (String(byIndex?.id) === normalizedId) {
                    return byIndex;
                }
            }
            const byId = row.columns.find((col) => String(col?.id) === normalizedId);
            if (byId) return byId;
            if (typeof idx === "number") return row.columns[idx];
            return undefined;
        },
        [columnIndexMap],
    );

    const sortedData = useMemo(() => {
        if (!sorting) {
            return tableData;
        }
        if (!sort) {
            return tableData;
        }
        const hasColumn = tableData.every((row) => Boolean(findRowColumnById(row, sort.id)));
        if (!hasColumn) {
            return tableData;
        }
        try {
            return [...tableData].sort((a, b) => {
                const columnAData = findRowColumnById(a, sort.id);
                const columnBData = findRowColumnById(b, sort.id);
                const columnA = columnAData?.sortBy ?? getComponentsValue(columnAData?.component);
                const columnB = columnBData?.sortBy ?? getComponentsValue(columnBData?.component);
                if (columnA < columnB) return sort.direction === "asc" ? -1 : 1;
                if (columnA > columnB) return sort.direction === "asc" ? 1 : -1;
                return 0;
            });
        } catch {
            return tableData;
        }
    }, [findRowColumnById, sorting, sort, tableData]);

    const clampWidth = (width: number) => Math.min(800, Math.max(72, width));

    const isSelectorValid = useCallback(
        (selector: any) => {
            if (!selector || typeof selector !== "object") return false;
            const keys = Object.keys(selector);
            if (keys.length !== columnKeys.length) return false;
            const keySet = new Set(columnKeys);
            for (const key of keys) {
                if (!keySet.has(key) || typeof selector[key] !== "boolean") return false;
            }
            return true;
        },
        [columnKeys],
    );

    const isWidthValid = useCallback(
        (sizes: any) => {
            if (!sizes || typeof sizes !== "object") return false;
            const keys = Object.keys(sizes);
            if (keys.length !== columnKeys.length) return false;
            const keySet = new Set(columnKeys);
            for (const key of keys) {
                const val = sizes[key];
                if (!keySet.has(key) || !Number.isFinite(Number(val))) return false;
            }
            return true;
        },
        [columnKeys],
    );

    const isOrderValid = useCallback(
        (order: any) => {
            if (!Array.isArray(order)) return false;
            if (order.length !== columnKeys.length) return false;
            const seen = new Set<string>();
            for (const key of order) {
                if (typeof key !== "string" || !columnKeys.includes(key) || seen.has(key)) {
                    return false;
                }
                seen.add(key);
            }
            return seen.size === columnKeys.length;
        },
        [columnKeys],
    );

    const defaultSelector = useMemo(() => {
        const selector: Record<string, boolean> = {};
        tableColumns.forEach((col, index) => {
            selector[getColumnKey(col, index)] = !col.hidden;
        });
        return selector;
    }, [tableColumns]);

    const getRowId = useCallback((row: ITableRow, fallback?: number) => {
        const rawId = (row as any)?.id ?? (row as any)?.rowId ?? fallback;
        if (typeof rawId === "object" && rawId !== null && "value" in rawId) {
            return String((rawId as any).value ?? (typeof fallback !== "undefined" ? fallback : ""));
        }
        if (typeof rawId === "string" || typeof rawId === "number") {
            return String(rawId);
        }
        return typeof fallback !== "undefined" ? String(fallback) : "";
    }, []);

    const getRowType = useCallback((row: ITableRow) => {
        const meta = (row as any)?.meta;
        if (meta?.kind) return String(meta.kind);
        if ((row as any)?.type) return String((row as any)?.type);
        return "row";
    }, []);

    const defaultSizes = useMemo(() => {
        const sizes: Record<string, number> = {};
        tableColumns.forEach((col, index) => {
            sizes[getColumnKey(col, index)] =
                typeof col.width === "number" ? clampWidth(col.width) : clampWidth(Number(col.width) || 240);
        });
        return sizes;
    }, [tableColumns]);

    const defaultOrder = useMemo(() => [...columnKeys], [columnKeys]);

    const defaultSort = useMemo(() => {
        const def = (table as any)?.defaultSortBy;
        if (def && typeof def.id !== "undefined" && (def.direction === "asc" || def.direction === "desc")) {
            const key = String(def.id);
            if (columnKeys.includes(key)) {
                return { id: key, direction: def.direction as "asc" | "desc" } as SortState;
            }
        }
        return undefined;
    }, [columnKeys, table]);

    const resetColumnsToDefault = useCallback(() => {
        setColumnSelector({ ...defaultSelector });
    }, [defaultSelector]);

    const resetOrderToDefault = useCallback(() => {
        setColumnOrder([...defaultOrder]);
    }, [defaultOrder]);

    const resetSizesToDefault = useCallback(() => {
        setColumnSize({ ...defaultSizes });
    }, [defaultSizes]);

    const resetSortToDefault = useCallback(() => {
        setSort(defaultSort ? ({ ...defaultSort } as SortState) : undefined);
    }, [defaultSort]);

    const resetAllToDefault = useCallback(() => {
        resetColumnsToDefault();
        resetOrderToDefault();
        resetSizesToDefault();
        resetSortToDefault();
    }, [resetColumnsToDefault, resetOrderToDefault, resetSizesToDefault, resetSortToDefault]);

    const loadCookieJSON = useCallback((key: string) => {
        try {
            const raw = Cookies.get(key);
            return raw ? JSON.parse(raw) : undefined;
        } catch {
            return undefined;
        }
    }, []);

    // Pagination skeleton (UI wiring to be added later)
    useEffect(() => {
        const dataLength = Math.max(0, sortedData.length);

        setPaginationState((prev) => {
            const nextPageSize = Math.max(1, prev.dataPerPage || DEFAULT_PAGE_SIZE);
            const pages = Math.max(1, Math.ceil((dataLength || 0) / nextPageSize));
            const nextCurrent = Math.min(Math.max(1, prev.currentPage), pages);

            return {
                currentPage: nextCurrent,
                dataPerPage: nextPageSize,
            };
        });
    }, [sortedData.length]);

    const paginationWindow = useMemo(() => {
        const pageSize = Math.max(1, paginationState.dataPerPage || sortedData.length || 1);
        const pages = Math.max(1, Math.ceil(Math.max(0, sortedData.length) / pageSize));
        const currentPage = Math.min(Math.max(1, paginationState.currentPage), pages);
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        return { start, end, pageSize, pages, currentPage };
    }, [sortedData.length, paginationState]);

    const paginationPayload: IPagination = useMemo(
        () => ({
            dataPerPage: paginationWindow.pageSize,
            pages: paginationWindow.pages,
            currentPage: paginationWindow.currentPage,
        }),
        [paginationWindow.currentPage, paginationWindow.pageSize, paginationWindow.pages],
    );

    const currentPageRows = useMemo(
        () => sortedData.slice(paginationWindow.start, paginationWindow.end),
        [sortedData, paginationWindow.end, paginationWindow.start],
    );

    const currentPageRowIds = useMemo(
        () =>
            currentPageRows
                .map((row, idx) => {
                    const isVisible = (row as any)?.visible !== false && (row as any)?.hidden !== true;
                    if (!isVisible) {
                        return "";
                    }
                    return getRowId(row, paginationWindow.start + idx);
                })
                .filter((id) => id),
        [currentPageRows, getRowId, paginationWindow.start],
    );

    const rowMetaById = useMemo(() => {
        const map = new Map<string, { row: ITableRow; order: number; rowType: string }>();
        sortedData.forEach((row, idx) => {
            const isVisible = (row as any)?.visible !== false && (row as any)?.hidden !== true;
            if (!isVisible) {
                return;
            }
            const rowId = getRowId(row, idx);
            if (!rowId) {
                return;
            }
            const order = Number.isFinite((row as any)?.order) ? Number((row as any)?.order) : idx;
            map.set(rowId, { row, order, rowType: getRowType(row) });
        });
        return map;
    }, [getRowId, getRowType, sortedData]);

    const selectedRowItems = useMemo(
        () => selectedItems.filter((item) => rowMetaById.has(item.id)),
        [rowMetaById, selectedItems],
    );

    useEffect(() => {
        if (!selectedItems.length) {
            return;
        }
        if (selectedRowItems.length !== selectedItems.length) {
            itemsControlDispatch(ItemsControlActions.updateSelectedItems(selectedRowItems));
        }
    }, [itemsControlDispatch, selectedItems, selectedRowItems]);

    const selectedIds = useMemo(() => selectedRowItems.map((item) => item.id), [selectedRowItems]);
    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const currentPageSelectedIds = useMemo(() => {
        const pageSet = new Set(currentPageRowIds);
        return selectedIds.filter((id) => pageSet.has(id));
    }, [currentPageRowIds, selectedIds]);

    const minimumNumberOfActions = useMemo(() => {
        if (!Array.isArray(tableData) || !tableData.length) {
            return 0;
        }
        return tableData.reduce((max, row) => {
            const count = Array.isArray(row?.actionsComponents) ? row.actionsComponents.length : 0;
            return Math.max(max, count);
        }, 0);
    }, [tableData]);

    const buildRowItem = useCallback(
        (rowId: string): IUniversalItem | null => {
            const meta = rowMetaById.get(rowId);
            if (!meta) {
                return null;
            }
            const { row, order, rowType } = meta;
            const isDraggable = (row as any)?.isDraggable === true;
            const isDroppable = (row as any)?.isDroppable === true;
            const replaceFlyCount = Math.max(1, selectedRowItems.length || 1);

            return {
                id: rowId,
                type: rowType,
                item: row,
                order,
                selectable: selection,
                draggable: isDraggable,
                droppable: isDroppable,
                acceptedTypes: acceptedDropTypes,
                replaceFly: <FlyContainer numberOfSelectedItems={replaceFlyCount} />,
                onDroppableDrop: (items) => onRowDrop?.(row, items),
                onIdle: () => onRowIdle?.(row, draggedItems),
            };
        },
        [acceptedDropTypes, draggedItems, onRowDrop, onRowIdle, rowMetaById, selectedRowItems.length, selection],
    );

    const handleMultiSelectChange = useCallback(
        (ids: string[]) => {
            const uniqueIds = Array.from(new Set(ids));
            const nextSelected = uniqueIds.map((id) => buildRowItem(id)).filter(Boolean) as IUniversalItem[];
            itemsControlDispatch(ItemsControlActions.updateSelectedItems(nextSelected));
        },
        [buildRowItem, itemsControlDispatch],
    );

    const toggleRowSelection = useCallback(
        (id: string) => {
            if (!id) return;
            const item = buildRowItem(id);
            if (!item) {
                return;
            }
            const isSelected = selectedRowItems.some((entry) => entry.id === id);
            const next = isSelected ? selectedRowItems.filter((entry) => entry.id !== id) : [...selectedRowItems, item];
            itemsControlDispatch(ItemsControlActions.updateSelectedItems(next));
        },
        [buildRowItem, itemsControlDispatch, selectedRowItems],
    );

    const handlePaginationUpdate = useCallback(
        (next: IPagination) => {
            setPaginationState(() => {
                const nextPageSize = Math.max(1, next.dataPerPage || 1);
                const pages = Math.max(1, Math.ceil(Math.max(0, sortedData.length) / nextPageSize));
                const nextCurrentPage = Math.min(Math.max(1, next.currentPage || 1), pages);
                return {
                    dataPerPage: nextPageSize,
                    currentPage: nextCurrentPage,
                };
            });
        },
        [sortedData.length],
    );

    const orderedColumnKeys = useMemo(() => {
        if (isOrderValid(columnOrder)) {
            return columnOrder;
        }
        return defaultOrder;
    }, [columnOrder, defaultOrder, isOrderValid]);

    const visibleOrderedKeys = useMemo(
        () => orderedColumnKeys.filter((key) => columnSelector[key] !== false),
        [columnSelector, orderedColumnKeys],
    );

    const getColumnWidth = useCallback(
        (columnKey: string, column: ITableHeader & { header: string }) => {
            const savedWidth = columnSize[columnKey];
            const parsedWidth = typeof column.width === "number" ? column.width : Number(column.width);
            const width = Number.isFinite(savedWidth) ? savedWidth : Number.isFinite(parsedWidth) ? parsedWidth : 240;
            return clampWidth(width);
        },
        [columnSize],
    );

    const persistSelector = useCallback(
        (selector: Record<string, boolean>) => {
            Cookies.set(`table-${tableName}-selector`, JSON.stringify(selector), { expires: 365, sameSite: "Lax" });
        },
        [tableName],
    );

    const persistWidths = useCallback(
        (sizes: Record<string, number>) => {
            Cookies.set(`table-${tableName}-width`, JSON.stringify(sizes), { expires: 365, sameSite: "Lax" });
        },
        [tableName],
    );

    const persistOrder = useCallback(
        (order: string[]) => {
            Cookies.set(`table-${tableName}-order`, JSON.stringify(order), { expires: 365, sameSite: "Lax" });
        },
        [tableName],
    );

    const persistSort = useCallback(
        (nextSort: SortState | undefined) => {
            if (nextSort) {
                Cookies.set(`table-${tableName}-sort`, JSON.stringify(nextSort), { expires: 365, sameSite: "Lax" });
            } else {
                Cookies.set(`table-${tableName}-sort`, JSON.stringify(null), { expires: 365, sameSite: "Lax" });
            }
        },
        [tableName],
    );

    // Load cookies and initialize state
    useEffect(() => {
        const selectorCookie = loadCookieJSON(`table-${tableName}-selector`);
        const widthCookie = loadCookieJSON(`table-${tableName}-width`);
        const orderCookie = loadCookieJSON(`table-${tableName}-order`);
        const sortCookie = loadCookieJSON(`table-${tableName}-sort`);

        const nextSelector = isSelectorValid(selectorCookie)
            ? (selectorCookie as Record<string, boolean>)
            : defaultSelector;
        const nextWidths = isWidthValid(widthCookie) ? (widthCookie as Record<string, number>) : defaultSizes;
        const nextOrder = isOrderValid(orderCookie) ? (orderCookie as string[]) : defaultOrder;

        setColumnSelector(nextSelector);
        setColumnSize(nextWidths);
        setColumnOrder(nextOrder);

        const cookieSortValid =
            sortCookie &&
            typeof sortCookie === "object" &&
            columnKeys.includes(String((sortCookie as any).id)) &&
            ((sortCookie as any).direction === "asc" || (sortCookie as any).direction === "desc");
        const initialSort = cookieSortValid
            ? ({ id: String((sortCookie as any).id), direction: (sortCookie as any).direction } as SortState)
            : defaultSort;
        setSort(initialSort);

        // Persist defaults back when cookies invalid
        if (!isSelectorValid(selectorCookie)) {
            persistSelector(nextSelector);
        }
        if (!isWidthValid(widthCookie)) {
            persistWidths(nextWidths);
        }
        if (!isOrderValid(orderCookie)) {
            persistOrder(nextOrder);
        }
        if (!cookieSortValid && initialSort) {
            persistSort(initialSort);
        }
        if (!cookieSortValid && !initialSort) {
            persistSort(undefined);
        }
    }, [
        columnKeySignature,
        columnKeys,
        defaultOrder,
        defaultSelector,
        defaultSizes,
        defaultSort,
        isOrderValid,
        isSelectorValid,
        isWidthValid,
        loadCookieJSON,
        persistOrder,
        persistSelector,
        persistSort,
        persistWidths,
        tableName,
    ]);

    // Persist selector changes
    useEffect(() => {
        if (!isSelectorValid(columnSelector)) return;
        persistSelector(columnSelector);
        // Selector change should also update order cookie to reflect reserved slots
        if (isOrderValid(columnOrder)) {
            persistOrder(columnOrder);
        }
    }, [columnSelector, columnOrder, isOrderValid, isSelectorValid, persistOrder, persistSelector]);

    // Persist width changes
    useEffect(() => {
        if (!isWidthValid(columnSize)) return;
        persistWidths(columnSize);
    }, [columnSize, isWidthValid, persistWidths]);

    // Persist order on change (committed on drop via stopColumnDrag)
    useEffect(() => {
        if (!isOrderValid(columnOrder)) return;
        if (columnDrag) return; // defer until drop
        persistOrder(columnOrder);
    }, [columnOrder, columnDrag, isOrderValid, persistOrder]);

    // Persist sort changes
    useEffect(() => {
        persistSort(sort);
    }, [persistSort, sort]);

    const onColumnSizeChange = (width: number, columnKey: string) => {
        const parsed = Number(width);
        if (!Number.isFinite(parsed)) return;
        const clamped = clampWidth(parsed);
        setColumnSize((prev) => ({ ...prev, [columnKey]: clamped }));
    };

    const onHeaderClick = (columnKey: string) => {
        if (!sorting) return;
        if (!columnIndexMap.has(columnKey)) return;
        let direction: "asc" | "desc" | undefined;
        if (sort && sort.id === columnKey) {
            direction = sort.direction === "asc" ? "desc" : undefined;
        } else {
            direction = "asc";
        }
        if (direction) {
            setSort({ id: columnKey, direction });
        } else {
            setSort(undefined);
        }
    };

    const setColumnRef = useCallback((key: string, node: HTMLDivElement | null) => {
        if (node) {
            columnRefs.current[key] = node;
        } else {
            delete columnRefs.current[key];
        }
    }, []);

    const computeInsertIndex = useCallback((clientX: number, orderedKeys: string[]) => {
        const candidates = orderedKeys
            .map((key) => {
                const el = columnRefs.current[key];
                if (!el) return null;
                const rect = el.getBoundingClientRect();
                return { key, midpoint: rect.left + rect.width / 2 };
            })
            .filter((entry): entry is { key: string; midpoint: number } => Boolean(entry));

        if (!candidates.length) {
            return 0;
        }

        for (let i = 0; i < candidates.length; i++) {
            if (clientX < candidates[i].midpoint) {
                return i;
            }
        }

        return candidates.length;
    }, []);

    const rebuildOrderWithVisible = useCallback(
        (baseOrder: string[], newVisibleOrder: string[]) => {
            const result: string[] = [];
            let visibleIdx = 0;
            baseOrder.forEach((key) => {
                if (columnSelector[key] === false) {
                    result.push(key);
                } else {
                    const replacement = newVisibleOrder[visibleIdx++];
                    if (replacement) {
                        result.push(replacement);
                    }
                }
            });
            return result;
        },
        [columnSelector],
    );

    const updateColumnOrderDuringDrag = useCallback(
        (clientX: number, columnKey: string) => {
            setColumnDrag((prev) =>
                prev ? { ...prev, currentX: clientX } : { key: columnKey, startX: clientX, currentX: clientX },
            );

            setColumnOrder((prev) => {
                const currentOrder = isOrderValid(prev) ? prev : defaultOrder;
                const visibleKeys = currentOrder.filter((key) => columnSelector[key] !== false);
                if (!visibleKeys.includes(columnKey)) {
                    setDropIndicatorIndex(null);
                    return currentOrder;
                }

                const withoutDrag = visibleKeys.filter((key) => key !== columnKey);
                const insertIndex = computeInsertIndex(clientX, withoutDrag);
                const clampedIndex = Math.max(0, Math.min(insertIndex, withoutDrag.length));
                const renderIndicatorIndex = clampedIndex === withoutDrag.length ? visibleKeys.length : clampedIndex;
                setDropIndicatorIndex(renderIndicatorIndex);

                const nextVisible = [...withoutDrag];
                nextVisible.splice(clampedIndex, 0, columnKey);

                const nextOrder = rebuildOrderWithVisible(currentOrder, nextVisible);
                return nextOrder;
            });
        },
        [columnSelector, computeInsertIndex, defaultOrder, isOrderValid, rebuildOrderWithVisible],
    );

    const clearColumnDragListeners = useCallback(() => {
        const { move, up } = columnDragListenersRef.current;
        if (move) {
            document.removeEventListener("pointermove", move);
        }
        if (up) {
            document.removeEventListener("pointerup", up);
        }
        columnDragListenersRef.current = {};
    }, []);

    const stopColumnDrag = useCallback(() => {
        clearColumnDragListeners();
        setDropIndicatorIndex(null);
        setColumnDrag(null);
        if (isOrderValid(columnOrder)) {
            persistOrder(columnOrder);
        }
    }, [clearColumnDragListeners, columnOrder, isOrderValid, persistOrder]);

    const startColumnDrag = useCallback(
        (event: ReactPointerEvent<HTMLElement>, columnKey: string) => {
            if (event.button !== 0) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const currentOrder = isOrderValid(columnOrder) ? columnOrder : defaultOrder;
            const visibleKeys = currentOrder.filter((key) => columnSelector[key] !== false);
            const currentIndex = visibleKeys.indexOf(columnKey);
            if (currentIndex >= 0) {
                setDropIndicatorIndex(currentIndex);
            }

            setColumnDrag({ key: columnKey, startX: event.clientX, currentX: event.clientX });

            const handleMove = (moveEvent: PointerEvent) => {
                moveEvent.preventDefault();
                updateColumnOrderDuringDrag(moveEvent.clientX, columnKey);
            };

            const handleUp = (upEvent: PointerEvent) => {
                upEvent.preventDefault();
                stopColumnDrag();
            };

            clearColumnDragListeners();
            columnDragListenersRef.current = { move: handleMove, up: handleUp };
            document.addEventListener("pointermove", handleMove);
            document.addEventListener("pointerup", handleUp);
        },
        [
            clearColumnDragListeners,
            columnOrder,
            columnSelector,
            defaultOrder,
            isOrderValid,
            stopColumnDrag,
            updateColumnOrderDuringDrag,
        ],
    );

    useEffect(() => {
        return () => {
            clearColumnDragListeners();
        };
    }, [clearColumnDragListeners]);

    const isOverflowing = tableRef.current ? tableRef.current.scrollWidth > tableRef.current.clientWidth : false;

    const renderableRows = useMemo(() => {
        return currentPageRows
            .map((row, index) => {
                const absoluteIndex = paginationWindow.start + index;
                const rowId = getRowId(row, absoluteIndex) || `row-${absoluteIndex}`;
                const isVisible = (row as any)?.visible !== false && (row as any)?.hidden !== true;
                if (!rowId || !isVisible) {
                    return null;
                }

                const rowColumns: Array<{ id: string; component: ReactNode; width: number }> = visibleOrderedKeys
                    .map((columnKey) => {
                        const columnIndex = columnIndexMap.get(columnKey);
                        if (typeof columnIndex !== "number") return null;
                        const column = tableColumns[columnIndex];
                        if (!column) return null;

                        const cell = findRowColumnById(row, columnKey);
                        const width = getColumnWidth(columnKey, column);

                        return {
                            id: cell?.id ? String(cell.id) : columnKey,
                            component: cell?.component ?? null,
                            width,
                        };
                    })
                    .filter((entry): entry is { id: string; component: ReactNode; width: number } => Boolean(entry));

                return {
                    row,
                    rowId,
                    columns: rowColumns,
                    absoluteIndex,
                    isOdd: index % 2 === 1,
                };
            })
            .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
    }, [
        columnIndexMap,
        findRowColumnById,
        getColumnWidth,
        getRowId,
        paginationWindow.start,
        tableColumns,
        visibleOrderedKeys,
        currentPageRows,
    ]);

    return (
        <>
            <div className="userful-table-wrapper">
                <div
                    className={`userful-table-body${isOverflowing ? "" : " userful-table-body-no-overflow"}`}
                    ref={tableRef}
                >
                    {selection && (
                        <div className="userful-table-left">
                            <div className="userful-table-left-header">
                                <TableMultiselect
                                    multiselect={table.multiselect}
                                    allItems={renderableRows.map(({ row }) => row)}
                                    selectedIds={currentPageSelectedIds}
                                    onChange={handleMultiSelectChange}
                                />
                            </div>
                            <div className="userful-table-left-rows">
                                {renderableRows.map(({ rowId, isOdd }) => {
                                    const isSelected = selectedIdSet.has(rowId);
                                    return (
                                        <div
                                            key={`${rowId}-select`}
                                            className={`userful-table-selection-cell ${isOdd ? "userful-table-row-odd" : "userful-table-row-even"} ${isSelected ? "userful-table-row-selected" : ""}`}
                                            data-click-and-drag-ignore
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                toggleRowSelection(rowId);
                                            }}
                                        >
                                            <Checkbox
                                                value={isSelected ? "selected" : "unselected"}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    toggleRowSelection(rowId);
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="userful-table-contents noselect">
                        <div className="userful-table-header-wrapper">
                            {visibleOrderedKeys.map((columnKey) => {
                                const columnIndex = columnIndexMap.get(columnKey);
                                if (typeof columnIndex !== "number") return null;
                                const column = tableColumns[columnIndex];
                                if (!column) return null;

                                const width = getColumnWidth(columnKey, column);
                                const isDraggingColumn = columnDrag?.key === columnKey;

                                return (
                                    <Fragment key={columnKey}>
                                        <div
                                            className={`userful-table-header ${isDraggingColumn ? "is-dragging" : ""}`}
                                            style={{ width, minWidth: width }}
                                            ref={(node) => setColumnRef(columnKey, node)}
                                        >
                                            <TableHeader
                                                width={width}
                                                header={column}
                                                sort={sort}
                                                onHeaderClick={() => onHeaderClick(columnKey)}
                                                onHeaderWidthChange={(_, w) => onColumnSizeChange(w, columnKey)}
                                                onDragHandlePointerDown={(event) => startColumnDrag(event, columnKey)}
                                                isDragging={isDraggingColumn}
                                            />
                                        </div>
                                    </Fragment>
                                );
                            })}
                            <TableHeaderFill />
                        </div>
                        <div className="userful-table-rows-wrapper">
                            {renderableRows.map(({ row, rowId, columns, isOdd, absoluteIndex }) => {
                                const rowItem = buildRowItem(rowId);
                                if (!rowItem) {
                                    return null;
                                }
                                return (
                                    <ClickAndDragItem item={rowItem} styles={{ width: "100%" }} key={rowId}>
                                        <TableRow
                                            key={rowId}
                                            row={row}
                                            rowId={rowId}
                                            columns={columns}
                                            isOdd={isOdd}
                                            rowIndex={absoluteIndex}
                                            onRowDoubleClick={onRowDoubleClick}
                                        />
                                    </ClickAndDragItem>
                                );
                            })}
                        </div>
                    </div>

                    <div className="userful-table-right">
                        <div className="userful-table-right-header">
                            <ColumnSelector
                                columns={columnSelector}
                                order={orderedColumnKeys}
                                minimumNumberOfActions={minimumNumberOfActions}
                                onColumnSelectorChange={(columns) => setColumnSelector(columns)}
                                onResetColumns={resetColumnsToDefault}
                                onResetOrder={resetOrderToDefault}
                                onResetSizes={resetSizesToDefault}
                                onResetSort={resetSortToDefault}
                                onResetAll={resetAllToDefault}
                            />
                        </div>
                        <div className="userful-table-right-rows">
                            {minimumNumberOfActions > 0 &&
                                renderableRows.map(({ row, rowId, isOdd }) => {
                                    const actions = Array.isArray((row as any)?.actionsComponents)
                                        ? ((row as any).actionsComponents as ReactNode[])
                                        : [];
                                    const paddedActions: Array<ReactNode | null> = Array.from(
                                        { length: Math.max(0, minimumNumberOfActions) },
                                        () => null,
                                    );
                                    for (let i = 0; i < actions.length && i < paddedActions.length; i++) {
                                        const slotIndex = paddedActions.length - 1 - i;
                                        paddedActions[slotIndex] = actions[i];
                                    }
                                    return (
                                        <div
                                            key={`${rowId}-actions`}
                                            className={`userful-table-actions-row ${isOdd ? "userful-table-row-odd" : "userful-table-row-even"}`}
                                        >
                                            {paddedActions.map((action, idx) => (
                                                <div
                                                    key={`${rowId}-action-${idx}`}
                                                    className="userful-table-action-cell"
                                                >
                                                    {action}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </div>

                <UserfulTablePagination
                    data={sortedData}
                    pagination={paginationPayload}
                    paginationOptions={[10, 15, 30, 50]}
                    onPaginationUpdate={handlePaginationUpdate}
                />
            </div>
        </>
    );
}
